import { useState } from "react";
import { useQuery } from "react-query";
import { useAppSelector } from "src/reducer/hooks";
import { selectProfile } from "src/reducer/profileSlice";

import { FeatureCode } from "@wallet-manager/node-types";
import {
  RentResourceBatchWalletStatus,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";

import {
  getTronRentRequestList,
  getTronRentRequestListExport,
  IGetTronRentRequestListParams,
  rentRequestTabRent,
} from "../../../../api/tronWalletResourceBalance";
import { useDatePicker } from "../../../../components/DatePicker";
import { useChains } from "../../../../components/FetchConfig";
import GeneralBtn from "../../../../components/GeneralBtn";
import LoadingDialog from "../../../../components/LoadingDialog";
import { Box, Container } from "../../../../components/MuiGenerals";
import {
  useAlerting,
  usePermission,
  useTabs,
  useTranslation,
} from "../../../../hooks";
import { tableConfig } from "../../../../utils/constant";
import {
  downloadFiles,
  findChainInfo,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../../utils/helper";
import { DivideLine, genField } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import {
  createZusInstance,
  useZusDialogStore,
} from "../../../../zustand/store";
import { AddressBookOrder } from "../../../AssetManagement/Swap/types";
import { DURATION_OPTIONS } from "../components/rent/DurationSingleSelection";
import StatusMultipleSelection from "../components/StatusMultipleSelection";
import WalletAddressMultipleTextField from "../components/WalletAddressMultipleTextField";
import WalletTypeMultipleSelection from "../components/WalletTypeMultipleSelection";
import { getEstimateBandwidthAmountByTxAmount } from "../helpers/getEstimateBandwidthAmountByTxAmount";
import { getEstimateEnergyAmountByTxAmount } from "../helpers/getEstimateEnergyAmountByTxAmount";
import RentDialog from "../RentDialog";
import { RentDialogFields } from "../types";
import RentRequestTabRentDialogContent from "./components/RentRequestTabRentDialogContent";
import RentRequestList from "./RentRequestList";

const translatePrefix = "tronWalletResourceBalance";

const initZusParams: IGetTronRentRequestListParams = {
  page: 0,
  pageSize: tableConfig.pageSize,

  dateCreatedFrom: "",
  dateCreatedTo: "",
  dateLastModifiedFrom: "",
  dateLastModifiedTo: "",

  chainType: null,
  chainId: null,
  status: [],
  walletTypes: [],
  walletAddress: [],
};

export const useZusParams =
  createZusInstance<IGetTronRentRequestListParams>(initZusParams);

function RentRequestTab() {
  const { tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();

  const tabsArr = [{ name: tc("table"), value: "" }];

  const { Tabs } = useTabs(tabsArr);

  const listRes = useQuery({
    queryKey: ["getRentRequestList", zusParams.body, zusParams.refetchCounter],
    queryFn: async () => {
      zusDialog.openExtra("loadingDialog");
      const res = await getTronRentRequestList(zusParams.body);
      zusDialog.closeExtra();

      return res;
    },
  });

  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar listRes={listRes} />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Tabs>
          <RentRequestList listMapping={listMapping} listRes={listRes} />
        </Tabs>
      </Container>
    </Box>
  );
}

export default RentRequestTab;

interface IFields {
  chainName: string;
  status: string[];
  batchId: string;
  walletType: string[];
  walletAddress: string;
}

const initFields: IFields = {
  chainName: "",
  status: [],
  batchId: "",
  walletAddress: "",
  walletType: [],
};

const initRentDialogFields: RentDialogFields = {
  chainName: "",
  receivingAddress: "",
  // amountEnergy: "",
  // amountBandwidth: "",
  numberOfTransactionEnergy: "",
  numberOfTransactionBandwidth: "",
  duration: "",
  addressBookOrder: {
    to_address: "",
    amount: "",
    to_wallet_tag: "",
    wallet_type: "",
    display_name: "",
  } as AddressBookOrder,
};

function FilterBar({ listRes }: { listRes: any }) {
  const [isRentDialogOpen, setIsRentDialogOpen] = useState(false);
  const [rentDialogFields, setRentDialogFields] =
    useState<RentDialogFields>(initRentDialogFields);

  const DateObj = {
    Created: useDatePicker(),
    Modified: useDatePicker(),
  };
  const { hasPermission } = usePermission();
  const [fields, setFields] = useState<IFields>(initFields);
  const { selectChainByPieces } = useChains();
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();

  const getChainNameApiSearchParams = (chain_name: string) => {
    return selectChainByPieces({
      chain_name: chain_name,
    });
  };

  const getParams = (): IGetTronRentRequestListParams => {
    const { pageSize } = tableConfig;
    const { chain_type, chain_id } = getChainNameApiSearchParams(
      fields.chainName
    );

    return {
      page: 0,
      pageSize: pageSize,
      status: fields.status,
      chainType: chain_type ? Number(chain_type) : null,
      chainId: chain_id ? Number(chain_id) : null,
      walletTypes: fields.walletType,
      walletAddress: fields.walletAddress
        ? fields.walletAddress.split(",")
        : [],
      dateCreatedFrom: DateObj.Created.start,
      dateCreatedTo: DateObj.Created.end,
      dateLastModifiedFrom: DateObj.Modified.start,
      dateLastModifiedTo: DateObj.Modified.end,
    };
  };
  const apiParams = getParams();
  const onSearch = () => {
    zusParams.setBody(apiParams);
    zusParams.refetch();
  };
  const onReset = () => {
    setFields(initFields);
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };

  const onExport = async () => {
    const count = listRes.data?.count || 0;
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getTronRentRequestListExport(
        { ...apiParams, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const res = listMapping("name", rawRes, { t, te, omitKeys: ["rawData"] });
    const config = {};
    downloadFiles(`Rent Request`, res, config);
  };

  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: rentDialogFields.chainName || "",
  });

  const validateForm = () => {
    if (!rentDialogFields.receivingAddress) {
      alerting("error", t("psReceivingAddress"));
      return false;
    }

    if (!rentDialogFields.numberOfTransactionEnergy) {
      alerting("error", t("phEnergyAmount"));
      return false;
    }

    if (!rentDialogFields.numberOfTransactionBandwidth) {
      alerting("error", t("phBandwidthAmount"));
      return false;
    }

    if (!rentDialogFields.duration) {
      alerting("error", t("psDuration"));
      return false;
    }

    return true;
  };

  const onRentDialogClose = () => {
    setIsRentDialogOpen(false);
    setRentDialogFields(initRentDialogFields);
  };

  const { merchantId } = useAppSelector(selectProfile);

  const onRentConfirm = async () => {
    const payload = {
      chainType: chain_type ? Number(chain_type) : null,
      chainId: chain_id ? Number(chain_id) : null,
      requiredEnergy: String(rentDialogFields.numberOfTransactionEnergy),
      requiredBandwidth: String(rentDialogFields.numberOfTransactionBandwidth),
      rentPeriodSecond: String(rentDialogFields.duration),
      walletAddress: String(rentDialogFields.receivingAddress),
    };

    if (!validateForm()) return;

    zusDialog.openExtra("loadingDialog");
    const res = await rentRequestTabRent(payload);
    zusDialog.closeExtra();

    if (!res) return;

    alerting("success", t("submitSuccess"));
    onRentDialogClose();
  };

  const F = genField({ t }, [
    ["creation_time", <DateObj.Created.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.Modified.Picker type="dateTime" />],
    // [
    //   "chainName",
    //   <ChainNameSingleSelection
    //     value={fields.chainName}
    //     onChange={(value) => {
    //       setFields((fields) => ({ ...fields, chainName: value }));
    //     }}
    //   />,
    // ],
    [
      "status",
      <StatusMultipleSelection
        value={fields.status}
        onChange={(value) => {
          setFields((fields) => ({ ...fields, status: value }));
        }}
      />,
    ],

    [
      "walletType",
      <WalletTypeMultipleSelection
        value={fields.walletType}
        onChange={(value) => {
          setFields((fields) => ({ ...fields, walletType: value }));
        }}
      />,
    ],
    [
      "wallet_address",
      <WalletAddressMultipleTextField
        label={t("phWallet_address")}
        value={fields.walletAddress}
        onChange={(e) => {
          setFields((fields) => ({ ...fields, walletAddress: e.target.value }));
        }}
      />,
    ],
  ]);

  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;

  return (
    <>
      <RentDialog
        isOpen={isRentDialogOpen}
        onClose={onRentDialogClose}
        onConfirm={onRentConfirm}
        content={
          <RentRequestTabRentDialogContent
            fields={rentDialogFields}
            setFields={setRentDialogFields}
          />
        }
      />
      <Box sx={customSx.filterCmdBar}>
        <GeneralBtn
          label="export"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
                .TabRentRequest.Export
            )
          }
          onClick={onExport}
        />
        <GeneralBtn
          label="rent"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
                .TabRentRequest.Rent
            )
          }
          onClick={() => setIsRentDialogOpen(true)}
          variant={"outlined"}
        />
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.last_modified_time}
        {/* {F.chainName} */}
        {F.status}
        {/* {F.batchId} */}
        {F.walletType}
        {F.wallet_address}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config: { t: any; te: any; omitKeys?: string[] }
): any[][] => {
  const { t, te, omitKeys = [] } = config;

  const res = array.map((item: any, index: number) => {
    const getDuration = () => {
      let durationInSecond: string | undefined = undefined;

      if (item.rent_energy_period_second) {
        durationInSecond = item.rent_energy_period_second;
      } else if (item.rent_bandwidth_period_second) {
        durationInSecond = item.rent_bandwidth_period_second;
      }

      if (durationInSecond) {
        if (durationInSecond === "0") {
          return durationInSecond;
        }

        return te(
          Object.entries(DURATION_OPTIONS).find(
            ([key, val]) => val === durationInSecond
          )?.[0]
        );
      }

      return "";
    };

    const duration = getDuration();

    const mappedResult = [
      // ["batchId", t("batchId"), item.batch_id],
      // ["stepSequence", t("stepSequence"), item.step_seq],
      // ["transactionSequence", t("transactionSequence"), item.tx_seq],
      ["orderId", t("orderId"), item.order_id],
      [
        "chainName",
        t("chainName"),
        findChainInfo(item.chain_type, item.chain_id)?.name || "",
      ],
      ["walletType", t("walletType"), te(WalletType[item.wallet_type])],
      ["wallet_address", t("wallet_address"), item.wallet_address],
      ["status", t("status"), te(RentResourceBatchWalletStatus[item.status])],
      ["requiredEnergy", t("requiredEnergy"), item.required_energy],
      ["requiredBandwidth", t("requiredBandwidth"), item.required_bandwidth],
      [
        "lastBandwidthBlockNumber",
        t("lastBandwidthBlockNumber"),
        item.last_bandwidth_block_number,
      ],
      [
        "lastEnergyBlockNumber",
        t("lastEnergyBlockNumber"),
        item.last_energy_block_number,
      ],
      ["duration", t("duration"), duration],
      ["created_date", t("created_date"), toDisplayTime(item.created_date)],
      [
        "last_modified_date",
        t("last_modified_date"),
        toDisplayTime(item.last_modified_date),
      ],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};
